import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { EnfafInputComponent } from '../../../../../components/enfaf-input/enfaf-input.component';
import { EnfafModalComponent } from '../../../../../components/enfaf-modal/enfaf-modal.component';
import { ApiService } from '../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../core/services/toast/toast.service';
import { getVimeoEmbedUrl } from '../../../../../core/utils/vimeo-url.util';
import { CourseContentComponent } from '../../../course-content/course-content.component';
import { AmbassadorVideoUrl } from '../../ambassador-info.component';

@Component({
  selector: 'app-modal-ambassador-info',
  imports: [EnfafModalComponent, ReactiveFormsModule, EnfafInputComponent],
  templateUrl: './modal-ambassador-info.component.html',
  styleUrl: './modal-ambassador-info.component.scss'
})
export class ModalAmbassadorInfoComponent implements OnInit, OnDestroy {
  protected readonly dialogRef = inject(MatDialogRef);
  protected readonly sanitizer = inject(DomSanitizer);
  protected readonly data?: AmbassadorVideoUrl = inject(MAT_DIALOG_DATA);
  protected readonly fb = inject(FormBuilder);
  protected readonly api = inject(ApiService);
  protected readonly toast = inject(ToastService);

  safeUrl: SafeResourceUrl | null = null;

  form = this.fb.group({
    title: this.fb.control<string | null>(null, [Validators.required]),
    vimeoId: this.fb.control<string | null>(null, [
      Validators.required,
      CourseContentComponent.noWhitespaceValidator,
      Validators.pattern(/^\d{9,10}$/)
    ])
  });

  isLoading = signal(false);

  private _destroyed$ = new Subject<void>();

  ngOnInit(): void {
    if (this.data) {
      this.form.patchValue({
        title: this.data.title,
        vimeoId: this.data.vimeoId
      });
      this.safeUrl = this.getVimeoUrl(this.data.vimeoId);
    }

    this.form.controls.vimeoId.valueChanges.pipe(takeUntil(this._destroyed$)).subscribe((vimeoId) => {
      if (vimeoId && vimeoId.length >= 9 && vimeoId.length <= 10) {
        this.safeUrl = this.getVimeoUrl(vimeoId);
      } else {
        this.safeUrl = null;
      }
    });
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  closeModal(): void {
    this.dialogRef.close();
  }

  async onConfirm(): Promise<void> {
    // Lógica para confirmar la acción en el modal
    console.log('Confirmar acción en el modal de información del embajador');
    if (!!this.data) {
      await this.updateVideo();
    } else {
      await this.createVideo();
    }
  }

  private async createVideo(): Promise<void> {
    if (this.form.invalid) {
      return;
    }

    const { title, vimeoId } = this.form.value;

    this.isLoading.set(true);

    try {
      const response = await firstValueFrom(
        this.api.createAmbassadorInfoVideo({
          title: title!,
          vimeoId: vimeoId!
        })
      );
      if (response.success) {
        this.toast.presentSuccessToast(response.message || 'Vídeo creado correctamente');
      } else {
        this.toast.presentErrorToast(response.message || 'Hubo un error creando el vídeo');
      }
      this.dialogRef.close(response.data);
    } catch (error) {
      const message = (error as any)?.error?.message;
      this.toast.presentErrorToast(message || 'Hubo un error creando el vídeo');
    } finally {
      this.isLoading.set(false);
    }
  }

  private async updateVideo(): Promise<void> {
    if (this.form.invalid || !this.data) {
      return;
    }

    const { title, vimeoId } = this.form.value;

    this.isLoading.set(true);

    try {
      const response = await firstValueFrom(
        this.api.updateAmbassadorInfoVideo(this.data.ambassadorVideoId, {
          title: title!,
          vimeoId: vimeoId!
        })
      );
      if (response.success) {
        this.toast.presentSuccessToast(response.message || 'Vídeo actualizado correctamente');
      } else {
        this.toast.presentErrorToast(response.message || 'Hubo un error actualizando el vídeo');
      }
      this.dialogRef.close(response.data);
    } catch (error) {
      const message = (error as any)?.error?.message;
      this.toast.presentErrorToast(message || 'Hubo un error actualizando el vídeo');
    } finally {
      this.isLoading.set(false);
    }
  }

  private getVimeoUrl(vimeoId: string): SafeResourceUrl {
    const url = getVimeoEmbedUrl(vimeoId) ?? '';
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }
}
